import { useMemo, useState } from 'react'
import { Search, X } from 'lucide-react'
import { cn } from '@/utils/cn'
import { gestorDashboardSearchClass } from '@/components/Gestores/gestorDashboardFormStyles'
import type { CriteriaOption } from '@/components/Gestores/gestorDashboardCriteriaUtils'

type Props = {
  label: string
  options: CriteriaOption[]
  selected: string[]
  emptyLabel?: string
  searchPlaceholder?: string
}

function normalizeSearch(value: string) {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
}

function filterCriteriaOptions(options: CriteriaOption[], query: string) {
  const normalizedQuery = normalizeSearch(query.trim())
  if (!normalizedQuery) return options
  return options.filter(
    (option) =>
      normalizeSearch(option.label).includes(normalizedQuery) ||
      normalizeSearch(option.value).includes(normalizedQuery),
  )
}

export function GestorDashboardCriteriaMultiSelect({
  label,
  options,
  selected,
  emptyLabel = 'Todos',
  searchPlaceholder = 'Buscar…',
  onChange,
}: Props & { onChange: (selected: string[]) => void }) {
  const [search, setSearch] = useState('')
  const allSelected = selected.length === 0

  const filteredOptions = useMemo(() => filterCriteriaOptions(options, search), [options, search])
  const showEmptyState = options.length === 0
  const showNoResults = !showEmptyState && filteredOptions.length === 0

  const toggleAll = () => onChange([])

  const toggleValue = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((item) => item !== value))
      return
    }
    onChange([...selected, value])
  }

  return (
    <div className='space-y-2'>
      <div className='flex items-center justify-between gap-2'>
        <span className='text-xs font-medium text-text-primary'>{label}</span>
        {!allSelected && (
          <span className='shrink-0 rounded-full bg-accent/15 px-2 py-0.5 text-[11px] font-semibold text-accent'>
            {selected.length}
          </span>
        )}
      </div>

      <div className='relative'>
        <Search
          size={14}
          className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted'
          aria-hidden
        />
        <input
          type='search'
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={searchPlaceholder}
          aria-label={`Buscar ${label.toLowerCase()}`}
          className={gestorDashboardSearchClass}
        />
        {search && (
          <button
            type='button'
            onClick={() => setSearch('')}
            className='absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-text-muted transition-colors hover:bg-white/[0.06] hover:text-text-primary'
            aria-label='Limpar busca'
          >
            <X size={13} />
          </button>
        )}
      </div>

      <div className='max-h-40 overflow-y-auto rounded-lg border border-[var(--glass-border)] bg-[var(--glass-bg)]/60 p-1.5 scrollbar-thin'>
        <label className='flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors hover:bg-white/[0.04]'>
          <input
            type='checkbox'
            checked={allSelected}
            onChange={toggleAll}
            className='h-4 w-4 rounded border-[var(--glass-border)] accent-accent'
          />
          <span className={cn('font-medium', allSelected ? 'text-accent' : 'text-text-primary')}>{emptyLabel}</span>
        </label>

        {showEmptyState ? (
          <p className='px-2.5 py-2 text-xs text-text-muted'>Nenhuma opção disponível no período.</p>
        ) : showNoResults ? (
          <p className='px-2.5 py-2 text-xs text-text-muted'>Nenhum resultado para &quot;{search.trim()}&quot;.</p>
        ) : (
          filteredOptions.map((option) => {
            const checked = !allSelected && selected.includes(option.value)
            return (
              <label
                key={option.value}
                className='flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors hover:bg-white/[0.04]'
              >
                <input
                  type='checkbox'
                  checked={checked}
                  onChange={() => {
                    if (allSelected) {
                      onChange([option.value])
                      return
                    }
                    toggleValue(option.value)
                  }}
                  className='h-4 w-4 shrink-0 rounded border-[var(--glass-border)] accent-accent'
                />
                <span className={cn('min-w-0 leading-snug', checked ? 'font-medium text-accent' : 'text-text-primary')}>
                  {option.label}
                </span>
              </label>
            )
          })
        )}
      </div>
    </div>
  )
}
